import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LifeBuoy, RefreshCw } from "lucide-react";

export interface ErrorStateDisplayProps {
  title: string;
  message?: string;
  variant?: "error" | "neutral";
  onRetry?: () => void;
  onContactSupport?: () => void;
}

const SUPPORT_EMAIL = "support@blacksmith.sh";

/**
 * Generic full-view "something went wrong" splash, styled as a pixel terminal:
 * a pixel display face, a Blacksmith-yellow accent rule, and a monospace status
 * line. Renders inside app chrome (not full screen), so it keeps the existing
 * `min-h-[60vh]` height. `variant` tints the status indicator.
 */
export function ErrorStateDisplay({
  title,
  message,
  variant = "error",
  onRetry,
  onContactSupport,
}: ErrorStateDisplayProps) {
  const accentDotClass =
    variant === "error"
      ? "text-red-500 dark:text-red-400"
      : "text-neutral-400 dark:text-neutral-500";

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <div className="relative flex min-h-[60vh] items-center justify-center overflow-hidden bg-app-background p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(var(--pattern_color)_0.5px,_transparent_0)] bg-[size:16px_16px] opacity-60 [--pattern_color:var(--color-black)]/15 dark:[--pattern_color:var(--color-white)]/7"
      />
      <div className="relative flex max-w-md flex-col items-center text-center">
        <div className="type-numerals-pixel-l text-neutral-900 dark:text-neutral-100">
          ERR
        </div>
        <div className="mt-2 h-1 w-16 rounded-full bg-blacksmith-yellow" />
        <h1 className="mt-8 type-page-title-m text-neutral-900 dark:text-neutral-100">
          {title}
        </h1>
        {message && (
          <p className="mt-2 type-body-s-regular text-muted-foreground">
            {message}
          </p>
        )}
        <p className="mt-3 type-code-block text-muted-foreground">
          status: 500 · event logged
          <span className={cn("ml-2", accentDotClass)}>●</span>
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button icon={RefreshCw} onClick={handleRetry}>
            Try again
          </Button>
          {onContactSupport ? (
            <Button
              variant="secondary"
              icon={LifeBuoy}
              onClick={onContactSupport}
            >
              Contact support
            </Button>
          ) : (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className={cn(buttonVariants({ variant: "secondary" }), "gap-1")}
            >
              <LifeBuoy className="h-4 w-4 shrink-0" />
              Contact support
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
